/**
 * useLanguage Hook
 * Manages language state and provides utilities for language management
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { 
  extractLanguageCode, 
  saveLanguage, 
  updateHtmlLang,
  getLanguageFromStorage,
  getLanguageFromCookie,
  getLanguageFromBrowser,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES
} from '../utils/languageUtils';

/**
 * Hook to manage language state and provide language utilities
 * @returns {Object} Language utilities and current language
 */
export const useLanguage = () => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Get current language code (2 letters)
  const currentLanguage = extractLanguageCode(i18n.language);

  /**
   * Change language and refresh content
   * @param {string} lang - Language code (e.g., 'fr', 'en')
   */
  const changeLanguage = async (lang) => {
    const code = extractLanguageCode(lang);
    
    if (!SUPPORTED_LANGUAGES.includes(code)) {
      console.warn(`Unsupported language: ${lang}, falling back to ${DEFAULT_LANGUAGE}`);
      return;
    }

    // Update i18n
    await i18n.changeLanguage(code);
    
    // Save to storage
    saveLanguage(code);
    
    // Update HTML lang attribute
    updateHtmlLang(code);
    
    // Invalidate all queries to re-fetch content with new language
    queryClient.invalidateQueries();
  };

  /**
   * Get current language with fallback detection
   * @returns {string} Current language code
   */
  const getCurrentLanguage = () => {
    return getLanguageFromStorage() || 
           getLanguageFromCookie() || 
           getLanguageFromBrowser() || 
           DEFAULT_LANGUAGE;
  };

  // Update HTML lang on mount and when language changes
  useEffect(() => {
    const lang = extractLanguageCode(i18n.language);
    updateHtmlLang(lang);
  }, [i18n.language]);

  return {
    currentLanguage,
    changeLanguage,
    getCurrentLanguage,
    isSupported: (lang) => SUPPORTED_LANGUAGES.includes(extractLanguageCode(lang)),
    supportedLanguages: SUPPORTED_LANGUAGES,
  };
};

export default useLanguage;

